import './LoginForm.scss';

import { Button, Input, Modal, Password, Snackbar } from '@bcmi-labs/react-components';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import AlertBox from '../../common/AlertBox';
import AppleIcon from '../../../assets/appleSSOIcon.svg';
import FacebookIcon from '../../../assets/facebookSSOIcon.svg';
import GithubIcon from '../../../assets/githubSSOIcon.svg';
import GoogleIcon from '../../../assets/googleSSOIcon.svg';
import { OverlayProvider } from 'react-aria';
import PropTypes from 'prop-types';
import authService from '../../../auth/authService';
import { datadogLogs } from '@datadog/browser-logs';

const LoginForm = (props) => {
  const { t } = useTranslation('login');
  const { isChild, isFromCoppa, googleAssistant } = props;
  const { handleSubmit, errors, control, formState, getValues } = useForm({
    mode: 'onChange',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingGithub, setLoadingGithub] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [loadingFacebook, setLoadingFacebook] = useState(false);
  const [cachedBrokenCoppaUser, setCachedBrokenCoppaUser] = useState(null);
  const [parentEmail, setParentEmail] = useState('');
  const [finalizeEmail, setFinalizeEmail] = useState('');
  const [teenConfirmation, setTeenConfirmation] = useState(false);
  const [childLoginAttempt, setChildLoginAttempt] = useState(0);
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    setFinalizeEmail(urlParams.get('finalizeEmail'));
    setTeenConfirmation(!!urlParams.get('teenConfirmation'));

    urlParams.delete('finalizeEmail');
    urlParams.delete('teenConfirmation');
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + '?' + urlParams.toString() + window.location.hash
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkCoppaUsername = async (username) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/v1/children/byUsername/${username}`, {
        method: 'HEAD',
      });
      return response.ok;
    } catch (e) {
      setLoading(false);
      setError(t('login:apiError'));
    }
  };

  // if is a broken coppa user redirect to the convert flow
  const loginBrokenCoppa = async () => {
    let brokenCoppaUser = cachedBrokenCoppaUser;
    if (!brokenCoppaUser) {
      try {
        brokenCoppaUser = await (
          await fetch(`https://content.arduino.cc/assets/broken-coppa-${process.env.REACT_APP_ENV || 'dev'}.json`)
        ).json();
      } catch (e) {
        if (process.env.REACT_APP_ENV) {
          datadogLogs.logger.error('Login error', {
            name: 'Cannot retrieve broken coppa user list',
            message: e,
          });
        }
        return false;
      }

      setCachedBrokenCoppaUser(brokenCoppaUser);
    }
    if (brokenCoppaUser.findIndex((e) => e.Nickname === getValues('username')) > -1) {
      // login the broken coppa account and redirect it to the conversion page
      authService.loginBrokenCoppa(getValues('username'), getValues('password'), (err) => {
        setLoading(false);
        setError(t(err.code));
      });
      return true;
    }
    return false;
  };

  const onLoginErrorCallback = async (err) => {
    if (await loginBrokenCoppa()) {
      return;
    }
    // Check if trying to log with a child account
    if (!isChild) {
      const flag = await checkCoppaUsername(getValues('username'));
      setLoading(false);
      if (flag) {
        if (!isFromCoppa) {
          window.location.replace(process.env.REACT_APP_EDU_PROFILE);
        } else {
          props.onViewSelection('kid-login');
          props.showChildAttempt(true);
        }
      } else {
        setError(t(err.code));
      }
    } else {
      setLoading(false);
      setError(t(err.code));
    }
  };

  const onCoppaLoginErrorCallback = async (err) => {
    if (await loginBrokenCoppa()) {
      return;
    }
    setChildLoginAttempt(childLoginAttempt + 1);
    setLoading(false);
    setError(t(err.code));
  };

  const onSubmit = (data) => {
    setLoading(true);
    setError('');
    props.showChildAttempt(false);
    if (isChild) {
      authService.loginCoppa(data.username, data.password, onCoppaLoginErrorCallback);
    } else {
      authService.login(data.username, data.password, onLoginErrorCallback);
    }
  };

  useEffect(() => {
    if (location.query && location.query.parentEmail) {
      setParentEmail(location.query.parentEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="login">
      <h1 className="arduino-typography-large">{isChild ? t('login:signInTitleJunior') : t('login:signInTitle')}</h1>
      {finalizeEmail || teenConfirmation ? (
        <AlertBox
          message={finalizeEmail ? t('login:finalizeChild', { finalizeEmail }) : t('login:teenConfirmation')}
          variant={'warning'}
        />
      ) : null}
      {googleAssistant ? <AlertBox message={t('login:googleAssistant')} variant="warning" /> : null}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-row">
          <Controller
            as={Input}
            name="username"
            control={control}
            defaultValue=""
            label={isChild ? t('login:childUsernameLabel') : t('login:usernameLabel')}
            error={errors.username && t('login:fieldRequired')}
            rules={{ required: true }}
            isRequired
          />
        </div>
        <div className="form-row">
          <Controller
            as={Password}
            name="password"
            defaultValue=""
            control={control}
            label={t('login:passwordLabel')}
            error={errors.password && t('login:fieldRequired')}
            rules={{ required: true }}
            isRequired
          />
        </div>
        <Link to={`/recover?isChild=${isChild}`} className="forgot-password">
          {t('login:forgotPassword')}
        </Link>
        {error ? <AlertBox message={error} /> : null}
        <Button type={'submit'} loading={loading} isDisabled={!formState.isValid}>
          {t('login:signIn')}
        </Button>
      </form>

      <div className="register-section">
        {t('login:noAccount')}
        <Link className="register-link" to="/register">
          {t('login:createAccount')}
        </Link>
      </div>
      {isChild ? null : (
        <div className="social-logins">
          <div className="social-divider">
            <span>{t('login:socialTitle')}</span>
          </div>
          <div className="social-link-container">
            <button
              className="social-button"
              disabled={loadingGoogle}
              aria-label="Sign-in with Google"
              onClick={() => {
                setLoadingGoogle(true);
                authService.loginWith(authService.GOOGLE);
              }}
            >
              <img src={GoogleIcon} alt="Sign-in with Google" />
              <span>Google</span>
            </button>
            <button
              className="social-button"
              aria-label="Sign-in with GitHub"
              disabled={loadingGithub}
              onClick={() => {
                setLoadingGithub(true);
                authService.loginWith(authService.GITHUB);
              }}
            >
              <img src={GithubIcon} alt="Sign-in with GitHub" />
              <span>GitHub</span>
            </button>
            <button
              className="social-button"
              aria-label="Sign-in with Facebook"
              disabled={loadingFacebook}
              onClick={() => {
                setLoadingFacebook(true);
                authService.loginWith(authService.FACEBOOK);
              }}
            >
              <img src={FacebookIcon} alt="Sign-in with Facebook" />
              <span>Facebook</span>
            </button>
            <button
              className="social-button"
              aria-label="Sign-in with Apple"
              disabled={loadingApple}
              onClick={() => {
                setLoadingApple(true);
                authService.loginWith(authService.APPLE);
              }}
            >
              <img src={AppleIcon} alt="Sign-in with Apple" />
              <span>Apple</span>
            </button>
          </div>
        </div>
      )}
      <Snackbar
        ActionElement={
          <span
            onClick={() => {
              setError('');
              props.onViewSelection('adult-login');
            }}
          >
            {t('login:loginHere')}
          </span>
        }
        message={t('login:regularLoginAttempt')}
        isOpen={childLoginAttempt > 1}
        onClose={() => setChildLoginAttempt(0)}
        turnOffAutoHide
        closeable
      ></Snackbar>
      {parentEmail ? (
        <OverlayProvider>
          <Modal
            isOpen={!!parentEmail}
            onClose={() => setParentEmail('')}
            isDismissable={false}
            title={null}
            primaryButton={{
              label: t('dialogAction'),
              closeModalOnClick: false,
              onClick: () => {
                setParentEmail('');
                history.replace({
                  query: {},
                });
              },
            }}
          >
            <div className="arduino-typography-x-small">
              <Trans i18nKey="login:dialogReminderBody">
                We sent an email with instructions to your parent’s email <strong>{{ parentEmail }}</strong>. You will
                be able to sign in once they finalize your account.
              </Trans>
            </div>
          </Modal>
        </OverlayProvider>
      ) : null}
      <div className="spacer"></div>
    </section>
  );
};

LoginForm.propTypes = {
  isFromCoppa: PropTypes.bool.isRequired,
  isChild: PropTypes.bool.isRequired,
  onViewSelection: PropTypes.func.isRequired,
  showChildAttempt: PropTypes.func.isRequired,
  googleAssistant: PropTypes.func.isRequired,
};

export default LoginForm;
